import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { useState } from "react";
import { harmonyRingEnabled } from "src/config/config";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { _get_user_from_localStorage } from "src/DAL/localStorage/localStorage";
import { useEffect } from "react";
import { EditProfileApiDashBoard } from "src/DAL/Profile/Profile";
import { memberDataQuery } from "src/Hooks/memberDataQuery";
import { cleanHtml } from "src/utils/cleanHtml";
import { useSnackbar } from "notistack";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import WeightWaistChart from "src/components/WeightWaistChart";
import DashboardFeedStories from "./CommunityFeeds/DashboardFeedStories";
import PaceCalculator from "./PaceCalculator";
import {
  CALCULATE_VDOT_VALUE,
  GET_VDOT,
  paceFromVdot,
} from "src/utils/constants";
import CustomPopoverNew from "src/components/CustomPopoverNew";
import AddWeight from "./WeightTracker/AddWeight";
import EditProfile from "./Profile/EditProfile";
import { get_harmony_ring_information_api } from "src/DAL/Programmes/Programmes";
import moment from "moment";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { gr } from "src/design-system/tokens";
import GrPage from "src/design-system/components/GrPage";
import GrLoader from "src/design-system/components/GrLoader";
import icons from "src/design-system/icons";
import GrIcon from "src/design-system/components/GrIcon";
import GrButton from "src/design-system/components/GrButton";
import GrCard from "src/design-system/components/GrCard";
import GrCardHeader from "src/design-system/components/GrCardHeader";
import GrSelect from "src/design-system/components/GrSelect";
import GrTaskRow from "src/design-system/components/GrTaskRow";
import { GrProgressRing } from "src/design-system/components/GrProgress";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import GrNotice from "src/design-system/components/GrNotice";
import GrListRow from "src/design-system/components/GrListRow";
import GrText from "src/design-system/components/GrText";
import { GrHero, GrHeroStat } from "src/design-system/components/GrHero";
import DashboardJourneysSummary, {
  useDashboardJourneys,
} from "./Dashboard/DashboardJourneysSummary";

const findPaceObjectByVdot = (vdotValue) => {
  return paceFromVdot.find((paceObject) => paceObject.vdot == vdotValue);
};

const useMemberData = (programId) => {
  return useQuery({
    // Shared with the sidebar's profile request (one call on the dashboard, not two).
    ...memberDataQuery(programId),
    // Changing the DAP filter keeps the dashboard on screen while the new list loads
    // (before, the whole page was replaced by the loader).
    keepPreviousData: true,
  });
};

const useHarmonyRingData = (programId, userId, enabled = false) => {
  const getTodayDate = () => new Date();
  const getOneWeekAgoDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date;
  };

  return useQuery({
    queryKey: ["harmonyRingData", programId, userId],
    queryFn: async () => {
      const postData = {
        start: moment(getOneWeekAgoDate()).format("YYYY-MM-DD"),
        end: moment(getTodayDate()).format("YYYY-MM-DD"),
      };

      const result = await get_harmony_ring_information_api(
        programId,
        userId,
        postData.start,
        postData.end
      );

      if (result.code === 200) {
        return result.harmony_ring_date;
      }
      throw new Error(result.message);
    },
    enabled: enabled && !!programId && !!userId,
  });
};

// Stable "no data yet" value: a new `{}` on every render while the profile loads made the effects
// that depend on memberData run (and set state) on every render, an update loop until the data came.
const NO_MEMBER_DATA = {};


// "Your Harmony Score" is hidden for now (27 Oct): set true to bring the card (and its data request) back.
const SHOW_HARMONY = false;

// Harmony Score: three compact rings (same data and labels as the old chart: Workout, Nutrition, Mindset).
const harmonyListSx = {
  m: 0,
  p: 0,
  listStyle: "none",
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: gr.space[4],
  "@media (max-width: 599.95px)": { gap: gr.space[2] },
};
const harmonyItemSx = {
  display: "flex",
  alignItems: "center",
  gap: gr.space[4],
  p: gr.space[3],
  borderRadius: gr.radius.tile,
  background: gr.color.tint,
  // Phones: three narrow tiles in one row, ring above the label.
  "@media (max-width: 599.95px)": {
    flexDirection: "column",
    gap: gr.space[2],
    px: gr.space[2],
    textAlign: "center",
    "& svg": { width: 56, height: 56 },
  },
};
const harmonyCategories = (data) => {
  const meta = {
    workout: { label: "Workout", color: gr.color.brand },
    food: { label: "Nutrition", color: gr.color.ok },
    mindset: { label: "Mindset", color: gr.color.brandDark },
  };
  return ["workout", "food", "mindset"].map((key) => {
    const d = data?.[key] || {};
    const completed = d.completed || 0;
    const total = completed + (d.not_completed || 0);
    return {
      key,
      ...meta[key],
      value: Math.round(d.percentage || 0),
      completed,
      total,
    };
  });
};

// Today's tasks (DAP): one row per task; only the list scrolls, the card title and filter stay in place.
// 480 ≈ six rows; a layout height, not a design token.
const dapScrollSx = {
  maxHeight: 480,
  overflowY: "auto",
  m: 0,
  p: 0,
  listStyle: "none",
  "& > li + li": { borderTop: `1px solid ${gr.color.line2}` },
};

// Task type from the same fields the calendar card uses for its icon.
const dapTaskType = (item) => {
  if (
    item?.practice_type === "workout" ||
    item?.library_type === "workout-type"
  )
    return "workout";
  if (item?.practice_type === "food" || item?.library_type === "meal-type")
    return "meal";
  if (
    item?.practice_type === "mindset" ||
    item?.library_type === "mindset-type"
  )
    return "mindset";
  if (item?.practice_type === "workbook") return "workbook";
  if (item?.practice_type === "video") return "video";
  if (item?.practice_type === "comment") return "comment";
  return "workout";
};

const dapTaskMeta = (item) => {
  const count = (item?.exercises || []).filter((e) => !e?.is_extra).length;
  if (dapTaskType(item) !== "workout" || !count) return null;
  return `${count} ${count === 1 ? "exercise" : "exercises"}`;
};

export default function DashboardApp() {
  const navigate = useNavigate();
  const { userInfo } = useContentSetting();
  const { contentSettingData, dashboardSettingData } = useContentSetting();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [checkedPace, setCheckedPace] = useState(true);
  const [isOpenInfo, setIsOpenInfo] = useState(false);
  const [dummyData, setDummyData] = useState([]);
  const [harmonyProgram, setHarmonyProgram] = useState([]);
  const [isShow, setIsShow] = useState(false);
  const [openWeight, setOpenWeight] = useState(false);
  const [userPre, setUserPre] = useState({});
  const [selectProgram, setSelectProgram] = useState("all");
  const [selectHaromny, setSelectHaromny] = useState("");
  const [chartList, setChartList] = useState([]);
  const [regularDayCalories, setRegularDayCalories] = useState(0);
  const [memberVdotData, setMemberVdotDataData] = useState({
    time_interval: 0,
    distance: "5km",
    easy_long: "e_km",
    thre_shold: "t_km",
    interval: "i_400_m",
  });

  const {
    data: memberData = NO_MEMBER_DATA,
    isLoading: isLoadingMember,
    isPreviousData: isDapRefreshing,
    isError: memberError,
    error: memberErrorData,
  } = useMemberData(selectProgram);

  // The member's journeys for the Your Journeys card (one request, loaded here).
  const { data: dashboardJourneys = [] } = useDashboardJourneys();

  const {
    data: programmesData = [],
    isLoading: isHarmonyLoading,
    isError: harmonyError,
    error: harmonyErrorData,
  } = useHarmonyRingData(
    selectHaromny,
    userInfo?._id,
    SHOW_HARMONY && !!selectHaromny
  );

  const updateProfileMutation = useMutation({
    mutationFn: async (formData) => {
      const result = await EditProfileApiDashBoard(formData, "dashboard");
      if (result.code === 200) {
        return result;
      }
      throw new Error(result.message);
    },
    onSuccess: (result) => {
      localStorage.setItem(`user_data`, JSON.stringify(result?.member));
      localStorage.setItem(`user_data_new`, JSON.stringify(result?.member));
      enqueueSnackbar("Pace calculator updated successfully", {
        variant: "success",
      });

      setMemberVdotDataData((inputs) => ({
        ...inputs,
        ["distance"]: checkedPace ? result?.member?.vdot?.race_type : "5km",
        ["time_interval"]: parseInt(
          result?.member?.vdot?.time_taken
            ? checkedPace
              ? result?.member?.vdot?.time_taken
              : 0
            : 0
        ),
      }));
      setIsShow(false);

      queryClient.invalidateQueries(["memberData"]);
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  let vdot = {};

  let vdot_parsed = {
    race_type: memberVdotData?.distance,
    time_taken: checkedPace ? memberVdotData?.time_interval : 0,
  };

  if (vdot_parsed.race_type && vdot_parsed.time_taken) {
    vdot = {
      race_type: vdot_parsed.race_type,
      time_taken: vdot_parsed.time_taken,
      distance_unit: "km",
    };
    let sorted_vdots = GET_VDOT;
    let race_type = "marathon";
    if (vdot_parsed.race_type == "marathon") {
      vdot.distance = 42.195;
      race_type = "marathon";
      sorted_vdots = GET_VDOT.sort((a, b) => a.marathon - b.marathon);
    } else if (vdot_parsed.race_type == "half_marathon") {
      vdot.distance = 21.098;
      race_type = "half_marathon";
      sorted_vdots = GET_VDOT.sort((a, b) => a.half_marathon - b.half_marathon);
    } else if (vdot_parsed.race_type == "5km") {
      vdot.distance = 5;
      race_type = "5km";
      sorted_vdots = GET_VDOT.sort((a, b) => a["5km"] - b["5km"]);
    } else if (vdot_parsed.race_type == "10km") {
      vdot.distance = 10;
      race_type = "10km";
      sorted_vdots = GET_VDOT.sort((a, b) => a["10km"] - b["10km"]);
    } else if (vdot_parsed.race_type == "15km") {
      vdot.distance = 15;
      race_type = "15km";
      sorted_vdots = GET_VDOT.sort((a, b) => a["15km"] - b["15km"]);
    }

    vdot.vdot_value = CALCULATE_VDOT_VALUE(
      race_type,
      vdot_parsed.time_taken,
      sorted_vdots
    );
  }

  const vdot33Object = findPaceObjectByVdot(
    vdot.vdot_value ? vdot.vdot_value : 0
  );

  const handleNavigate = () => {
    const selectedProgram = harmonyProgram.find(
      (program) => program.program_id === selectHaromny
    );
    if (!selectedProgram) return;
    navigate(`/programmes/calendar/${selectedProgram.program_slug}`, {
      state: {
        ...selectedProgram,
        workout_title: selectedProgram.programme_title,
        title: selectedProgram.programme_title,
        _id: {
          _id: selectedProgram.program_id,
          is_harmony_ring_enabled: true,
        },
        open_dashboard: false,
      },
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    let VDOT = {
      race_type: checkedPace ? memberVdotData?.distance : "5km",
      time_taken: checkedPace ? memberVdotData?.time_interval : 0,
    };

    const formData = new FormData();
    formData.append("pace_calculator", checkedPace);
    formData.append("vdot", JSON.stringify(VDOT));

    if (checkedPace && memberVdotData?.time_interval == 0) {
      return enqueueSnackbar("Please enter a value in the Duration field.", {
        variant: "error",
      });
    }

    updateProfileMutation.mutate(formData);
  };

  const handleChange = (event) => {
    setSelectProgram(event.target.value);
  };

  const handleChangeHaromny = (event) => {
    setSelectHaromny(event.target.value);
  };

  useEffect(() => {
    if (memberError) {
      enqueueSnackbar(memberErrorData?.message, { variant: "error" });
    }
  }, [memberError, memberErrorData, enqueueSnackbar]);

  useEffect(() => {
    if (harmonyError) {
      enqueueSnackbar(harmonyErrorData?.message, { variant: "error" });
    }
  }, [harmonyError, harmonyErrorData, enqueueSnackbar]);

  useEffect(() => {
    if (memberData && Object.keys(memberData).length > 0) {
      setChartList(memberData?.graph_weights || []);
      setUserPre(memberData?.user_preferences || {});

      let isCheck = memberData?.pace_calculator;
      setMemberVdotDataData((inputs) => ({
        ...inputs,
        ["distance"]: isCheck ? memberData?.vdot?.race_type : "5km",
        ["time_interval"]: parseInt(
          memberData?.vdot?.time_taken
            ? isCheck
              ? memberData?.vdot?.time_taken
              : 0
            : 0
        ),
      }));
      setCheckedPace(isCheck);

      const harmonyProgrammes =
        memberData?.programme_array?.filter(
          (item) =>
            harmonyRingEnabled && item.is_harmony_ring_enabled === true,
        ) || [];
      setHarmonyProgram(harmonyProgrammes);

      if (harmonyProgrammes.length > 0 && !selectHaromny) {
        setSelectHaromny(harmonyProgrammes[0]?.program_id);
      }
    }
  }, [memberData, selectHaromny]);

  useEffect(() => {
    const requiredFields = [
      "first_name",
      "last_name",
      "email",
      "dob",
      "street",
      "state",
      "city",
      "country",
      "zip_code",
      "time_zone",
      "gender",
      "weight",
      "height",
      "age",
      "activity_level",
    ];

    let isAnyFieldMissing = requiredFields.some((field) => !userInfo?.[field]);

    if (userInfo?.gender === "female" && !userInfo?.last_cycle_date) {
      isAnyFieldMissing = true;
    }
    if (
      userInfo?.deficit_percentage &&
      !userInfo?.deficit_value &&
      !userInfo?.days_for_deficit
    ) {
      isAnyFieldMissing = true;
    }

    if (isAnyFieldMissing) {
      setIsOpenInfo(true);
    } else {
      setIsOpenInfo(false);
    }
  }, [userInfo]);

  useEffect(() => {
    setMemberVdotDataData((prevState) => {
      return {
        ...prevState,
        ["pace"]: vdot33Object
          ? vdot33Object[prevState?.easy_long + "_low"]
          : null,
        ["paceUp"]: vdot33Object
          ? vdot33Object[prevState?.easy_long + "_up"]
          : null,
        ["pace1"]: vdot33Object ? vdot33Object[prevState?.thre_shold] : null,
        ["pace2"]: vdot33Object ? vdot33Object[prevState?.interval] : null,
      };
    });
  }, [
    memberVdotData?.easy_long,
    memberVdotData?.thre_shold,
    memberVdotData?.interval,
    vdot?.vdot_value,
  ]);
  const storedUserInfo = _get_user_from_localStorage();

  useEffect(() => {
    const caloriesValue = Number(storedUserInfo?.regular_day_calories);
    setRegularDayCalories(Number.isFinite(caloriesValue) ? caloriesValue : 0);
    // The stored user is parsed again on every render (new object), so depend on the value only.
  }, [storedUserInfo?.regular_day_calories]);

  useEffect(() => {
    setDummyData([
      {
        color: "#fff",
        title: "Total Coins",
        tooltip: "Total Coins",
        count: memberData?.total_points ? memberData?.total_points : 0,
        icon: "mdi:currency-usd-circle",
        link: true,
        to: `/dashboard-coins-info`,
      },
      {
        color: "#fff",
        title: "Total Daily Coins (AP)",
        tooltip: "Total Daily Coins (Accountability Partner)",
        count: memberData?.reward_points?.today_coins
          ? memberData?.reward_points?.today_coins
          : 0,
        icon: "mdi:cash-multiple",
        link: false,
      },
      {
        color: "#fff",
        title: "",
        count: "Shopping List",
        tooltip: "Shopping List",
        icon: "mdi:cart-outline",
        link: true,
        link_text: "Create Shopping List →",
        to: `/dashboard/shopping-list`,
      },
    ]);
  }, [memberData, regularDayCalories]);

  if (isLoadingMember) {
    return <GrLoader sx={{ py: gr.space[12] }} />;
  }

  const [coinsCard, dailyCoinsCard, shoppingCard] = dummyData;
  const formatCount = (count) =>
    typeof count === "string"
      ? count
      : (Math.round((count || 0) * 10) / 10).toLocaleString("en-IE");

  return (
    <GrPage
      sx={{
        display: "grid",
        gap: gr.space[6],
        maxWidth: 1240,
        mx: "auto",
        pb: gr.space[8],
      }}
    >
      <GrHero
        stats={
          coinsCard ? (
            <>
              <GrHeroStat
                label={coinsCard.title}
                value={formatCount(coinsCard.count)}
                hint={
                  <GrButton
                    variant="link"
                    component={RouterLink}
                    to={coinsCard.to}
                  >
                    View history →
                  </GrButton>
                }
              />
              {dailyCoinsCard ? (
                <GrHeroStat
                  label="Daily Coins (AP)"
                  value={formatCount(dailyCoinsCard.count)}
                  hint={dailyCoinsCard.tooltip
                    ?.replace("Total Daily Coins ", "")
                    .replace(/[()]/g, "")}
                />
              ) : null}
            </>
          ) : null
        }
      >
        {contentSettingData && contentSettingData.dashboard_description ? (
          <Box
            className="gr-hero-text"
            sx={{
              // Same sizes as every page title (24) and its line under it (14).
              "& h1, & h2": {
                m: 0,
                fontSize: `${gr.font.size.h2} !important`,
                fontWeight: gr.font.weight.semibold,
                lineHeight: 1.2,
              },
              "& p": {
                m: 0,
                mt: gr.space[1],
                fontSize: gr.font.size.md,
                opacity: 0.95,
              },
            }}
            dangerouslySetInnerHTML={{
              // Admin HTML, cleaned before it is shown (rule 18).
              __html: cleanHtml(contentSettingData.dashboard_description),
            }}
          />
        ) : (
          <Box
            component="h2"
            sx={{
              m: 0,
              fontSize: gr.font.size.h2,
              fontWeight: gr.font.weight.semibold,
              lineHeight: 1.2,
            }}
          >
            {dashboardSettingData?.wellcome_text}
          </Box>
        )}
      </GrHero>

      {memberData?.gender === "female" && memberData?.is_menstrual_cycle ? (
        <GrNotice
          tone="warning"
          icon={<GrIcon icon={icons.alert} />}
          title="Gentle Reminder"
          text="You’re on your menstrual cycle — it’s completely normal to feel less strong during this time. Please listen to your body, take it easy, and allow yourself to rest if needed. Your well-being comes first."
        />
      ) : null}

      {/* Your Journeys on the left; Shopping List over the Daily Action Plan on the right, both columns
          the same height from 900px (7 : 5 like the rows below). Phones: one column. A member with no
          journey gets the right column across the full width. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          "@media (min-width: 900px)": { gridTemplateColumns: "minmax(0, 7fr) minmax(0, 5fr)" },
          gap: gr.space[6],
          alignItems: "stretch",
          "& > :only-child": { gridColumn: "1 / -1" },
        }}
      >
        {/* Quick review of the member's journeys; renders nothing when they have no journey. */}
        <DashboardJourneysSummary journeys={dashboardJourneys} />

        <Box
          sx={{
            display: "grid",
            gap: gr.space[6],
            minWidth: 0,
            // Shopping (if shown) as tall as it needs, the DAP down to the row's end.
            gridTemplateRows: shoppingCard ? "auto 1fr" : "1fr",
          }}
        >
          {shoppingCard ? (
            <GrCard padding="sm" as="div">
              <GrListRow
                icon={<GrIcon icon={icons.cart} />}
                title={shoppingCard.count}
                action={
                  <GrButton
                    variant="link"
                    component={RouterLink}
                    to={shoppingCard.to}
                  >
                    {shoppingCard.link_text}
                  </GrButton>
                }
                sx={{ py: 0 }}
              />
            </GrCard>
          ) : null}

          <GrCard>
            <GrCardHeader
              as="h2"
              title="Daily Action Plan (DAP)"
              // One line; the Programme select drops under the title when there is no room.
              noWrapTitle
              action={
                <GrSelect
                  compact
                  label="Programme"
                  value={selectProgram}
                  onChange={handleChange}
                  sx={{ width: { xs: "100%", sm: 240 }, minWidth: 0 }}
                  options={[
                    { value: "all", label: "All" },
                    ...(memberData?.programme_array || []).map((item) => ({
                      value: item.program_id,
                      label: item.programme_title,
                    })),
                  ]}
                />
              }
              wrapAction
            />
            <Box
              aria-busy={isDapRefreshing}
              sx={{
                position: "relative",
                transition: "opacity .15s ease",
                ...(isDapRefreshing && { opacity: 0.5, pointerEvents: "none" }),
              }}
            >
              {isDapRefreshing ? (
                <GrLoader
                  size="sm"
                  inline
                  label="Loading tasks"
                  sx={{
                    position: "absolute",
                    top: gr.space[4],
                    left: "50%",
                    ml: `calc(${gr.icon.lg} / -2)`,
                    zIndex: 1,
                  }}
                />
              ) : null}
              {memberData?.today_workout &&
              memberData?.today_workout?.length > 0 ? (
                <>
                  {isLoadingMember ? (
                    <GrLoader />
                  ) : (
                    <Box component="ul" sx={dapScrollSx}>
                      {memberData?.today_workout.map((item, index) => (
                        <Box component="li" key={item?._id || index}>
                          <GrTaskRow
                            type={dapTaskType(item)}
                            title={item?.title}
                            programme={item?.program_info?.title}
                            meta={dapTaskMeta(item)}
                            done={Boolean(item?.workout_completed)}
                            // Same destination as the calendar card (WorkOutList).
                            onClick={() =>
                              navigate(
                                `/programmes/calendar/${item?.program_info?.program_slug}`,
                                {
                                  state: {
                                    ...item,
                                    workout_title: item?.title,
                                    open_dashboard: true,
                                  },
                                }
                              )
                            }
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              ) : (
                <GrEmptyState
                  compact
                  icon={<GrIcon icon={icons.calendar} />}
                  title="No tasks for today! Have a great day—stay healthy and happy!"
                />
              )}
            </Box>
          </GrCard>
        </Box>
      </Box>

      {/* Community Feed across the full width, straight on the page (no card behind it): as many story
          tiles as fit. */}
      <Box component="section" aria-label="Community Feed" sx={{ minWidth: 0 }}>
        <GrCardHeader
          as="h2"
          title="Community Feed"
          action={
            <GrButton variant="link" component={RouterLink} to="/community-area">
              Go To Community Feed →
            </GrButton>
          }
        />
        <DashboardFeedStories />
      </Box>


      {SHOW_HARMONY && harmonyProgram && harmonyProgram.length > 0 && (
        <GrCard>
          <GrCardHeader
            as="h2"
            title="Your Harmony Score"
            subtitle="The graph shows your task completion data from the past 7 days."
            action={
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  columnGap: gr.space[5],
                  rowGap: gr.space[2],
                  minWidth: 0,
                }}
              >
                {/* Same style as the other cards' header links (Go To Weight Tracker →). */}
                <GrButton variant="link" onClick={handleNavigate}>
                  Go to Programme →
                </GrButton>
                <GrSelect
                  compact
                  label="Programme"
                  value={selectHaromny}
                  onChange={handleChangeHaromny}
                  sx={{ width: { xs: "100%", sm: 340 }, minWidth: 0 }}
                  options={harmonyProgram.map((item) => ({
                    value: item.program_id,
                    label: item.programme_title,
                  }))}
                />
              </Box>
            }
            wrapAction
          />
          <Box component="ul" sx={harmonyListSx} aria-busy={isHarmonyLoading}>
            {harmonyCategories(programmesData).map((c) => (
              <Box component="li" key={c.key} sx={harmonyItemSx}>
                <GrProgressRing
                  value={isHarmonyLoading ? 0 : c.value}
                  size={72}
                  stroke={8}
                  color={c.color}
                  // No tasks of this type in the past 7 days: a dash instead of a misleading 0%.
                  centre={!isHarmonyLoading && !c.total ? "–" : undefined}
                  ariaLabel={
                    c.total
                      ? `${c.label}: ${c.completed} of ${c.total} done, ${c.value}%`
                      : `${c.label}: no tasks in the past 7 days`
                  }
                />
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      fontSize: gr.font.size.md,
                      fontWeight: gr.font.weight.semibold,
                      color: gr.color.ink,
                    }}
                  >
                    {c.label}
                  </Box>
                  {!isHarmonyLoading ? (
                    <Box
                      sx={{ fontSize: gr.font.size.sm, color: gr.color.ink2 }}
                    >
                      {c.total
                        ? `${c.completed} of ${c.total} done`
                        : "No tasks in the past 7 days"}
                    </Box>
                  ) : null}
                </Box>
              </Box>
            ))}
          </Box>
        </GrCard>
      )}

      {/* Weight Tracker and Pace Calculator side by side (7 : 5) from 900px, the same height; stacked
          on phones. */}
      <Box
        sx={{
          display: "grid",
          gap: gr.space[6],
          gridTemplateColumns: "minmax(0, 1fr)",
          "@media (min-width: 900px)": { gridTemplateColumns: "minmax(0, 7fr) minmax(0, 5fr)" },
          alignItems: "stretch",
        }}
      >
        <GrCard>
          <GrCardHeader
            as="h2"
            title="Weight Tracker"
            subtitle="Your Last 30-Day Progress Summary"
            action={
              <GrButton
                variant="link"
                component={RouterLink}
                to="/weight-tracker"
              >
                Go To Weight Tracker →
              </GrButton>
            }
          />
          <WeightWaistChart
            chartList={chartList}
            userPre={userPre}
            height={300}
            compact
          />
        </GrCard>

        <GrCard>
          <GrCardHeader
            as="h2"
            title="Pace Calculator"
            action={
              <FormControlLabel
                className="me-0"
                sx={{ mr: 0 }}
                label=""
                control={
                  <Switch
                    checked={checkedPace}
                    inputProps={{ "aria-label": "Show pace calculator" }}
                    onChange={(e) => {
                      setCheckedPace(e.target.checked);
                      setIsShow(true);
                    }}
                  />
                }
              />
            }
          />
          <GrText variant="secondary">
            If running is part of your programme, get your current paces by
            entering a recent time trial result. These will show
            automatically inside your programme.
          </GrText>
          <PaceCalculator
            checkedPace={checkedPace}
            memberVdotData={memberVdotData}
            setMemberVdotDataData={setMemberVdotDataData}
            handleUpdate={handleUpdate}
            isFormLoading={updateProfileMutation.isLoading}
            setIsShow={setIsShow}
            isShow={isShow}
          />
        </GrCard>
      </Box>

      <CustomPopoverNew
        isOpenPop={openWeight}
        isClosePop={setOpenWeight}
        title={"Check In"}
        componentToPassDown={
          <AddWeight
            openWeight={openWeight}
            setOpenWeight={setOpenWeight}
            weight_list={() => queryClient.invalidateQueries(["memberData"])}
            editValues={null}
            userPre={userPre}
          />
        }
      />
      <CustomPopoverNew
        isOpenPop={isOpenInfo}
        title={"Update Profile"}
        is_hide_icon={true}
        componentToPassDown={
          <EditProfile isOpenInfo={isOpenInfo} setIsOpenInfo={setIsOpenInfo} />
        }
      />
    </GrPage>
  );
}
